import { useMemo } from "react";
import type { CustomTableColumn, TableFilterState, TableSortState } from "../types";
import {
	getColumnValue,
	getLooseDataIndex,
	getLooseColumnKey,
	getLooseSorter,
	toValueArray,
} from "../utils/columnUtils";
import { getColumnBehavior } from "../utils/tableHelpers";
import type { LooseColumn } from "../types";

type UseProcessedDataParams<T extends object> = {
	baseColumns: CustomTableColumn<T>[];
	dataSource?: T[];
	filterState: TableFilterState;
	sortState: TableSortState | null;
	searchTerm: string;
};

export function useProcessedData<T extends object>(params: UseProcessedDataParams<T>) {
	const { baseColumns, dataSource, filterState, sortState, searchTerm } = params;

	return useMemo(() => {
		let rows = (dataSource || []) as T[];
		const activeFilters = Object.entries(filterState).filter(([, selected]) => selected.length > 0);

		if (activeFilters.length > 0 || sortState || searchTerm) {
			rows = [...rows];

			activeFilters.forEach(([colKey, selected]) => {
				const col = baseColumns.find((c) => getLooseColumnKey(c as LooseColumn<T>) === colKey);
				if (!col || !getColumnBehavior(col).enableFilter) {
					return;
				}
				const looseCol = col as LooseColumn<T>;

				rows = rows.filter((row) => {
					const rowValues = toValueArray(getColumnValue(row, getLooseDataIndex(looseCol)));
					return rowValues.some((v) => selected.includes(v));
				});
			});

			if (sortState) {
				const sortCol = baseColumns.find((c) => getLooseColumnKey(c as LooseColumn<T>) === sortState.columnKey);
				if (sortCol && getColumnBehavior(sortCol).enableSort) {
					const looseSortCol = sortCol as LooseColumn<T>;
					rows.sort((a, b) => {
						const sorter = getLooseSorter(looseSortCol);
						let result = 0;

						if (typeof sorter === "function") {
							result = sorter(a, b);
						} else {
							const aValue = toValueArray(getColumnValue(a, getLooseDataIndex(looseSortCol)))[0] || "";
							const bValue = toValueArray(getColumnValue(b, getLooseDataIndex(looseSortCol)))[0] || "";
							result = aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: "base" });
						}

						return sortState.order === "ascend" ? result : -result;
					});
				}
			}

			const keyword = searchTerm.trim().toLowerCase();
			if (keyword) {
				rows = rows.filter((row) => {
					return baseColumns.some((col) => {
						if (typeof col.title !== "string" || col.title.toLowerCase() === "action") {
							return false;
						}
						const values = toValueArray(getColumnValue(row, getLooseDataIndex(col as LooseColumn<T>)));
						return values.some((v) => v.toLowerCase().includes(keyword));
					});
				});
			}
		}

		return rows;
	}, [baseColumns, dataSource, filterState, searchTerm, sortState]);
}
